import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  SHOP_CART_MAX_QUANTITY,
  SHOP_DISCREET_NOTICE,
  SHOP_SHIPPING_NOTICE,
  US_STATE_CODES,
  centsLabel,
  parseShopShipTo,
  type ShopCartLine,
  type ShopProduct,
  type ShopProductSummary,
  type ShopShipTo,
} from "@gooner/shared";
import { anonymousAnalyticsId, track } from "../../analytics";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { addToShopCart, rememberPendingCheckout, setShopCartQuantity, useShopCart } from "../../lib/shopCart";
import { useShopCatalog } from "../../lib/shopOpen";
import { AddToCartButton } from "../components/AddToCartButton";
import { AuthModal } from "../components/AuthModal";
import { Button, Field, Select, TextInput } from "../ui/primitives";
import { ShopBackLink } from "../components/ShopBackLink";

const EMPTY_ADDRESS: ShopShipTo = { name: "", line1: "", line2: "", city: "", state: "", zip: "" };

/** "400: That one is sold out" reads better without the status. */
const errorText = (err: unknown) =>
  err instanceof Error ? err.message.replace(/^\d{3}:\s*/, "") : "Checkout didn't start. Try again.";

/** `/shop/cart` — what is in the cart, the note about paying, and the form that leads to checkout. */
export function ShopCart() {
  const user = useSession((s) => s.user);
  const cart = useShopCart();
  const [products, setProducts] = useState<Map<string, ShopProduct> | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const slugs = useMemo(() => cart.map((l) => l.slug).sort().join(","), [cart]);

  // Asked per product rather than from the catalog: an unlisted product can be in a cart.
  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      cart.map((l) => userApi.shopProduct(l.slug).then((p) => p, () => null)),
    ).then((found) => {
      if (cancelled) return;
      // Gone from the catalog means gone from the cart: it could only fail at checkout.
      cart.forEach((l, i) => {
        if (!found[i]) setShopCartQuantity(l.slug, 0);
      });
      setProducts(new Map(found.filter((p): p is ShopProduct => !!p).map((p) => [p.slug, p])));
    });
    return () => {
      cancelled = true;
    };
    // The quantities do not change what a product is, so only the set of slugs re-asks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugs]);

  const lines = products ? cart.filter((l) => products.has(l.slug)) : [];
  const product = (l: ShopCartLine) => products?.get(l.slug) as ShopProduct;
  const totalCents = lines.reduce((sum, l) => sum + product(l).priceCents * l.quantity, 0);
  const units = lines.reduce((n, l) => n + l.quantity, 0);
  const soldOut = lines.some((l) => product(l).soldOut);
  const notice = lines.length > 0 ? product(lines[0] as ShopCartLine).cartNotice : "";
  const { catalog } = useShopCatalog();
  const cardMinimum = catalog?.cardMinimumCents ?? 0;
  const belowCardMinimum = cardMinimum > 0 && totalCents < cardMinimum;
  // The cheapest things not already in the cart: the quickest way over the card minimum.
  const suggestions = belowCardMinimum
    ? (catalog?.products ?? [])
        .filter((p) => !p.soldOut && !cart.some((l) => l.slug === p.slug))
        .sort((a, b) => a.priceCents - b.priceCents)
        .slice(0, 4)
    : [];

  useEffect(() => {
    if (products) track("shop_cart_viewed", { items: units, cents: totalCents, below_card_min: belowCardMinimum });
    // Once per load of the cart, not on every quantity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products === null]);

  // No account needed: a guest gets a private link to the order instead.
  const checkout = () => {
    track("shop_checkout_opened", { items: units, cents: totalCents });
    setCheckingOut(true);
  };
  const signIn = () => {
    track("shop_signin_prompted", { items: units, cents: totalCents });
    setAuthOpen(true);
  };

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <ShopBackLink />
      <h1 className="mt-3 text-2xl font-semibold text-zinc-100">Your cart</h1>

      {products && lines.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-500">Your cart is empty.</p>
      ) : null}
      {!products && cart.length > 0 ? <div className="min-h-[40dvh]" /> : null}

      {lines.length > 0 ? (
        <>
          <ul className="mt-4 divide-y divide-[var(--color-edge)]">
            {lines.map((l) => {
              const p = product(l);
              return (
                <li key={l.slug} className="flex gap-3 py-3">
                  <Link to={`/shop/${p.slug}`} className="h-20 w-16 shrink-0 overflow-hidden rounded bg-[var(--color-panel-2)]">
                    {p.images[0] ? <img src={p.images[0]} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/shop/${p.slug}`} className="block text-sm font-medium text-zinc-100 hover:text-[var(--color-accent-soft)]">
                      {p.name}
                    </Link>
                    <p className="text-sm text-zinc-400">{centsLabel(p.priceCents)}</p>
                    {p.soldOut ? <p className="text-sm text-red-400">Sold out. Remove it to check out.</p> : null}
                    <div className="mt-2 flex items-center gap-2">
                      <Button size="sm" aria-label={`One less ${p.name}`} onClick={() => setShopCartQuantity(l.slug, l.quantity - 1)}>
                        -
                      </Button>
                      <span className="w-6 text-center text-sm text-zinc-100" aria-label="Quantity">
                        {l.quantity}
                      </span>
                      <Button
                        size="sm"
                        aria-label={`One more ${p.name}`}
                        disabled={l.quantity >= SHOP_CART_MAX_QUANTITY}
                        onClick={() => setShopCartQuantity(l.slug, l.quantity + 1)}
                      >
                        +
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShopCartQuantity(l.slug, 0)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                  <p className="shrink-0 text-sm text-zinc-100">{centsLabel(p.priceCents * l.quantity)}</p>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex items-baseline justify-between border-t border-[var(--color-edge)] pt-4">
            <span className="text-zinc-300">Total</span>
            <span className="text-2xl font-semibold text-[var(--color-accent)]">{centsLabel(totalCents)}</span>
          </div>
          <p className="mt-1 text-sm text-zinc-500">Shipping included. US addresses only. {SHOP_SHIPPING_NOTICE}</p>
          <p className="text-sm text-zinc-500">{SHOP_DISCREET_NOTICE}</p>
          {cardMinimum > 0 ? (
            <CardNudge totalCents={totalCents} minimumCents={cardMinimum} suggestions={suggestions} />
          ) : notice ? (
            <p className="mt-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-3 text-sm text-zinc-200">
              {notice}
            </p>
          ) : null}

          {checkingOut ? (
            <CheckoutForm lines={lines} totalCents={totalCents} units={units} disabled={soldOut} />
          ) : (
            <Button variant="primary" className="mt-5 w-full" disabled={soldOut} onClick={checkout}>
              Checkout
            </Button>
          )}
          {!user ? (
            <p className="mt-3 text-center text-sm text-zinc-400">
              No account needed.{" "}
              <button type="button" onClick={signIn} className="text-zinc-200 underline hover:text-zinc-100">
                Sign in
              </button>{" "}
              to keep this order on your account.
            </p>
          ) : null}
        </>
      ) : null}

      {authOpen ? (
        <AuthModal
          initialMode="login"
          reason="Sign in to keep this order on your account."
          onClose={() => setAuthOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * The card minimum as a target rather than a warning: how far off the cart is, and the
 * cheapest things that close the gap, each one click from the cart.
 */
function CardNudge({
  totalCents,
  minimumCents,
  suggestions,
}: {
  totalCents: number;
  minimumCents: number;
  suggestions: ShopProductSummary[];
}) {
  const short = minimumCents - totalCents;
  if (short <= 0) {
    return (
      <p className="mt-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-3 text-sm text-zinc-200">
        You can pay by card or crypto.
      </p>
    );
  }
  const done = Math.max(0, Math.min(100, Math.round((totalCents / minimumCents) * 100)));
  return (
    <div className="mt-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-3">
      <p className="text-sm text-zinc-100">
        Add <span className="font-semibold text-[var(--color-accent)]">{centsLabel(short)}</span> more to pay by card. Any
        order can be paid with crypto.
      </p>
      <div
        className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-edge)]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={done}
        aria-label="Progress to paying by card"
      >
        <div className="h-full rounded-full bg-[var(--color-accent)]" style={{ width: `${done}%` }} />
      </div>
      {suggestions.length > 0 ? (
        <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {suggestions.map((p) => (
            <li key={p.slug} className="flex flex-col">
              <Link to={`/shop/${p.slug}`} className="block flex-1">
                <div className="aspect-[4/5] overflow-hidden rounded bg-[var(--color-panel)]">
                  {p.images[0] ? <img src={p.images[0]} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-zinc-200">{p.name}</p>
                <p className="text-xs text-zinc-400">{centsLabel(p.priceCents)}</p>
              </Link>
              <AddToCartButton
                size="sm"
                className="mt-1 w-full"
                onAdd={() => {
                  addToShopCart(p.slug);
                  track("shop_cart_added", { slug: p.slug, cents: p.priceCents, from: "cart_suggestion" });
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CheckoutForm({
  lines,
  totalCents,
  units,
  disabled,
}: {
  lines: ShopCartLine[];
  totalCents: number;
  units: number;
  disabled: boolean;
}) {
  const signedIn = useSession((s) => !!s.user);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof ShopShipTo) => (e: { target: { value: string } }) => {
    setError(null);
    setAddress((a) => ({ ...a, [k]: e.target.value }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseShopShipTo(address);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // A guest's paid event can only join their funnel if the server knows this browser's id.
      const analyticsId = signedIn ? undefined : (anonymousAnalyticsId() ?? undefined);
      const r = await userApi.shopCheckout({ items: lines, email, shipTo: parsed.shipTo, analyticsId });
      track("shop_checkout_started", { items: units, cents: totalCents });
      rememberPendingCheckout(r.transactionId, r.orderKey);
      window.location.assign(r.checkoutUrl);
    } catch (err) {
      const status = err instanceof Error ? /^(\d{3}):/.exec(err.message)?.[1] : undefined;
      track("shop_checkout_failed", { items: units, status: status ?? "unknown" });
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="mt-5 space-y-3" autoComplete="on">
      <Field label="Name for the parcel">
        <TextInput required autoComplete="name" value={address.name} onChange={set("name")} />
      </Field>
      <Field label="Street address">
        <TextInput required autoComplete="address-line1" value={address.line1} onChange={set("line1")} />
      </Field>
      <Field label="Apartment, unit (optional)">
        <TextInput autoComplete="address-line2" value={address.line2} onChange={set("line2")} />
      </Field>
      <Field label="City">
        <TextInput required autoComplete="address-level2" value={address.city} onChange={set("city")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="State">
          <Select required autoComplete="address-level1" value={address.state} onChange={set("state")}>
            <option value="">Choose</option>
            {US_STATE_CODES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="ZIP code">
          <TextInput required inputMode="numeric" autoComplete="postal-code" value={address.zip} onChange={set("zip")} />
        </Field>
      </div>
      <Field label="Email for your receipt">
        <TextInput required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <p className="text-xs text-zinc-500">
        We keep your address encrypted and delete it 30 days after your order ships. Your email goes
        to the payment processor for your receipt; we keep only a one-way fingerprint of it, so you
        can find your order later with its number and this email.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="primary" className="w-full" disabled={busy || disabled}>
        {busy ? "Starting checkout…" : `Pay ${centsLabel(totalCents)}`}
      </Button>
    </form>
  );
}
